// CN-R40B reports paging and complete-export helpers.
// Pure. Does not classify report outcomes.

export const REPORTS_PAGE_SIZE = 50;
export const REPORTS_EXPORT_CHUNK_SIZE = 500;

export const EXPORT_INCOMPLETE_MESSAGE =
  "Unable to export the complete report. Please try again.";

export type ReportsQueryFilters = {
  dateRangePreset: string;
  month: string;
  year: string;
  customStartDate: string;
  customEndDate: string;
  sourceType: string;
  status: string;
  outcome: string;
};

export type ReportsPaging = {
  limit: number;
  offset: number;
};

export function getReportsOffset(page: number, pageSize: number): number {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safeSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 1;
  return (safePage - 1) * safeSize;
}

export function getReportsTotalPages(total: number, pageSize: number): number {
  const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  const safeSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 1;
  return Math.max(1, Math.ceil(safeTotal / safeSize));
}

export function buildReportsUrl(
  filters: ReportsQueryFilters,
  paging: ReportsPaging
): string {
  const params = new URLSearchParams();

  params.set("dateRangePreset", filters.dateRangePreset);
  params.set("sourceType", filters.sourceType);
  params.set("status", filters.status);
  params.set("outcome", filters.outcome);
  params.set("limit", String(paging.limit));
  params.set("offset", String(paging.offset));

  if (filters.dateRangePreset === "month" && filters.month) {
    params.set("month", filters.month);
  }

  if (filters.dateRangePreset === "year" && filters.year) {
    params.set("year", filters.year);
  }

  if (filters.dateRangePreset === "custom") {
    if (filters.customStartDate) {
      params.set("customStartDate", filters.customStartDate);
    }

    if (filters.customEndDate) {
      params.set("customEndDate", filters.customEndDate);
    }
  }

  return `/api/portal/community-navigator/reports?${params.toString()}`;
}

export type ExportChunk<T> = {
  items: T[];
  total: number;
  requestedLimit: number;
};

export type ExportReduceState<T> = {
  rows: T[];
  expectedTotal: number | null;
};

export type ExportReduceResult<T> =
  | {
      ok: true;
      rows: T[];
      expectedTotal: number;
      complete: boolean;
    }
  | {
      ok: false;
      error: string;
    };

export function reduceExportChunk<T>(
  state: ExportReduceState<T>,
  chunk: ExportChunk<T>
): ExportReduceResult<T> {
  const items = Array.isArray(chunk.items) ? chunk.items : [];
  const total = Number(chunk.total);

  if (!Number.isFinite(total) || total < 0) {
    return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
  }

  const expectedTotal =
    state.expectedTotal === null ? total : state.expectedTotal;

  if (total !== expectedTotal) {
    return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
  }

  if (items.length === 0 && state.rows.length < expectedTotal) {
    return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
  }

  if (state.rows.length + items.length > expectedTotal) {
    return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
  }

  const nextRows = state.rows.concat(items);

  if (
    items.length < chunk.requestedLimit &&
    nextRows.length < expectedTotal
  ) {
    return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
  }

  return {
    ok: true,
    rows: nextRows,
    expectedTotal,
    complete: nextRows.length === expectedTotal,
  };
}

export async function collectReportExportRows<T>(args: {
  chunkSize: number;
  fetchChunk: (
    offset: number,
    limit: number
  ) => Promise<{ items: T[]; total: number }>;
}): Promise<{ ok: true; rows: T[] } | { ok: false; error: string }> {
  let rows: T[] = [];
  let expectedTotal: number | null = null;
  let offset = 0;
  const chunkSize = args.chunkSize;
  const maxIterations =
    chunkSize > 0 ? Math.floor(1000000 / chunkSize) + 2 : 1;

  for (let iteration = 0; iteration < maxIterations; iteration += 1) {
    let payload: { items: T[]; total: number };

    try {
      payload = await args.fetchChunk(offset, chunkSize);
    } catch {
      return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
    }

    const reduced = reduceExportChunk(
      { rows, expectedTotal },
      {
        items: payload.items,
        total: payload.total,
        requestedLimit: chunkSize,
      }
    );

    if (!reduced.ok) {
      return reduced;
    }

    rows = reduced.rows;
    expectedTotal = reduced.expectedTotal;

    if (reduced.complete) {
      return { ok: true, rows };
    }

    const loadedCount = Array.isArray(payload.items) ? payload.items.length : 0;

    if (loadedCount <= 0) {
      return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
    }

    offset += loadedCount;
  }

  return { ok: false, error: EXPORT_INCOMPLETE_MESSAGE };
}
